import React, { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import i18next from 'i18next';
import { DifmButton } from '@yola/webapp-plugins';
import config from 'src/js/modules/config';
import user from 'src/js/modules/user';
import legacyUrlsMap from 'src/js/router/helpers/legacy-urls-map';
import routesMap from 'src/js/router/helpers/routes-map';
import MainMenu from 'src/js/common/components/main-menu';
import {
  hasGoldSubscription,
  hasGoldSubscriptionWithDifmPromo,
  hasGoldSubscriptionWithoutDifmPromo,
} from 'src/js/modules/difm/verifiers/has-gold-subscription';
import isDifmOrderSubmitted from 'src/js/modules/difm/verifiers/is-difm-order-submitted';
import useAvailablePlatformComponents from 'src/js/modules/user/hooks/use-available-platform-components';
import NavbarHeader from 'src/js/common/components/navbar-header';
import segment from 'src/js/modules/analytics/segment';
import useFeatureFlags from 'src/js/modules/feature-flags/hooks/use-feature-flags';
import isUrlMatchesRoutes from '../helpers/is-url-matches-routes';
import dialogs from '../../dialogs';

const {
  constants: { platformComponents },
} = user;

const {
  constants: { events },
  track,
} = segment;

const dashboardRoutes = [routesMap.dashboard.pattern(), routesMap.home.pattern()];

function NavigationContainer() {
  const availablePlatformComponents = useAvailablePlatformComponents();
  const onlineStoreUrl = useSelector(config.selectors.getOnlineStoreUrl);
  const analyticsUrl = useSelector(config.selectors.getAnalyticsUrl);
  const helpCenterConfig = useSelector(config.selectors.getHelpCenterConfig);
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [featureFlags] = useFeatureFlags(['nn_difm_promo']);
  const { nn_difm_promo: isNnDifmPromoFeatureFlagEnabled } = featureFlags;

  const isGold = hasGoldSubscription();
  const isGoldWithDifmPromo = hasGoldSubscriptionWithDifmPromo();
  const isGoldWithoutDifmPromo = hasGoldSubscriptionWithoutDifmPromo();
  const isOrderSubmitted = isDifmOrderSubmitted();
  const isDifmButtonDisabled =
    !isNnDifmPromoFeatureFlagEnabled || isGoldWithoutDifmPromo || (!isGold && isOrderSubmitted);
  const isChangeRequestFlow = isGoldWithDifmPromo && isOrderSubmitted;
  const isOrderFormFlow = isGoldWithDifmPromo && !isOrderSubmitted;

  const onDifmButtonClick = () => {
    if (isChangeRequestFlow) {
      track(events.DIFM_CHANGE_REQUEST_BUTTON_CLICKED);

      // Since we use href to redirect to help center, we don't need to show any dialogs
      return;
    }

    if (isOrderFormFlow) {
      track(events.DIFM_BUTTON_CLICKED);

      dispatch(dialogs.actions.show(dialogs.dialogTypes.DIFM_ORDER_FORM_DIALOG));
      return;
    }

    track(events.DIFM_BUTTON_CLICKED);
    dispatch(dialogs.actions.show(dialogs.dialogTypes.DIFM_UPSELL_DIALOG));
  };

  const navigateToDashboard = () => {
    navigate(routesMap.dashboard.url());
  };

  const { pathname = '' } = location;

  const siteTrafficMenu = useMemo(() => {
    const menu = [];

    if (availablePlatformComponents.includes(platformComponents.ANALYTICS)) {
      menu.push({
        title: i18next.t('Analytics'),
        href: analyticsUrl,
      });
    }

    if (availablePlatformComponents.includes(platformComponents.TRAFFIC_BUILDER)) {
      menu.push({
        title: i18next.t('Traffic Builder'),
        href: legacyUrlsMap.trafficBuilder(),
      });
    }

    if (availablePlatformComponents.includes(platformComponents.SITEWIT)) {
      menu.push({
        title: i18next.t('SiteWit'),
        href: legacyUrlsMap.sitewit(),
      });
    }

    return menu;
  }, [availablePlatformComponents, analyticsUrl]);

  return (
    <React.Fragment>
      <NavbarHeader.NavItem
        selected={isUrlMatchesRoutes(dashboardRoutes, pathname)}
        onClick={navigateToDashboard}
        label={i18next.t('Dashboard')}
      />
      {availablePlatformComponents.includes(platformComponents.DOMAINS) && (
        <NavbarHeader.NavItem
          target="_self"
          href={legacyUrlsMap.domains()}
          label={i18next.t('Domains')}
        />
      )}
      {availablePlatformComponents.includes(platformComponents.EMAIL) && (
        <NavbarHeader.NavItem
          target="_self"
          href={legacyUrlsMap.email()}
          label={i18next.t('Email')}
        />
      )}
      {availablePlatformComponents.includes(platformComponents.ECOMMERCE) && (
        <NavbarHeader.NavItem href={onlineStoreUrl} label={i18next.t('Online Store')} />
      )}
      {Boolean(siteTrafficMenu.length) && (
        <MainMenu
          captions={{
            name: i18next.t('Site Traffic'),
          }}
          className="ws-mainmenu--site-traffic"
          items={siteTrafficMenu}
        />
      )}
      {!isDifmButtonDisabled && (
        <DifmButton
          {...(isChangeRequestFlow && {
            href: helpCenterConfig.nn_difm_request_changes_url,
          })}
          isAnimated={!isGoldWithDifmPromo}
          withPopover={isChangeRequestFlow}
          onClick={onDifmButtonClick}
        />
      )}
    </React.Fragment>
  );
}

export default NavigationContainer;
